<template>
  <Page>
    <template #card-header>
      <h4 class="text-center">{{ $t('pageTitles.login') }}</h4>
    </template>
    <ValidationObserver v-slot="{ invalid }">
      <b-form class="login-form" @submit.prevent="handleLogin">
        <div v-if="hasToken" class="auth-tabs" data-cy="auth-tabs">
          <router-link
            :to="pinLoginLink"
            class="auth-tab"
            :class="{ active: isPinLogin }"
            data-cy="tab-pin"
          >
            {{ $t('buttons.pinLoginTab') }}
          </router-link>
          <router-link
            :to="emailLoginLink"
            class="auth-tab"
            :class="{ active: !isPinLogin }"
            data-cy="tab-email"
          >
            {{ $t('buttons.emailLoginTab') }}
          </router-link>
        </div>

        <KeyPad
          v-if="isPinLogin && hasPin"
          v-model="password"
          data-cy="login-pin-pad"
          @input="pinLengthCheck"
        />

        <div
          v-else-if="isPinLogin && hasPin === false"
          class="auth-setup"
          data-cy="pin-setup"
        >
          <p class="auth-setup__copy">{{ $t('messages.noPinSetup') }}</p>
          <router-link
            class="auth-setup__link"
            data-cy="pin-setup-link"
            :to="{ name: 'ForgotPin', query: { ...$route.query, setup: '1' } }"
          >
            {{ $t('buttons.setUpPin') }}
          </router-link>
        </div>

        <div v-else-if="isPinLogin" class="auth-loading" data-cy="pin-loading">
          <b-spinner small />
        </div>

        <div
          v-else-if="hasToken && hasPassword === false"
          class="auth-setup"
          data-cy="password-setup"
        >
          <p class="auth-setup__copy">{{ $t('messages.noPasswordSetup') }}</p>
          <router-link
            class="auth-setup__link"
            data-cy="password-setup-link"
            :to="{
              name: 'ForgotPassword',
              query: { ...$route.query, setup: '1' }
            }"
          >
            {{ $t('buttons.setUpPassword') }}
          </router-link>
        </div>

        <div
          v-else-if="hasToken && hasPassword === null"
          class="auth-loading"
          data-cy="password-loading"
        >
          <b-spinner small />
        </div>

        <div v-else>
          <ValidationProvider
            v-slot="{ errors }"
            name="email"
            rules="required|email"
          >
            <b-form-group :label="$t('labels.email')">
              <b-input
                v-model="email"
                data-cy="login-email"
                type="email"
                trim
                lazy
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            v-slot="{ errors }"
            name="password"
            rules="required"
          >
            <b-form-group id="password" :label="$t('labels.password')">
              <b-input
                v-model="password"
                data-cy="login-password"
                type="password"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </ValidationProvider>

          <b-button
            block
            data-cy="login-button"
            variant="themed-primary"
            :disabled="invalid"
            type="submit"
          >
            <span v-if="!busy"> {{ $t('buttons.login') }} </span>
            <b-spinner v-else small />
          </b-button>
        </div>

        <div class="h-flex account-links">
          <div v-if="merchant && merchant.id">
            <div data-cy="merchant-register" class="login-help">
              <router-link :to="{ name: 'MerchantCustomerRegistration' }">
                {{ $t('buttons.register') }}
              </router-link>
            </div>
          </div>

          <div v-if="showLoginHelp" data-cy="login-help" class="login-help">
            <router-link
              :to="{ name: loginHelpDestination, query: $route.query }"
            >
              {{ $t('buttons.loginHelp') }}
            </router-link>
          </div>
        </div>

        <div v-if="!isPinLogin" data-cy="send-link" class="send-link">
          <router-link :to="{ name: 'SendLoginLink' }">
            {{ $t('buttons.sendMeLoginLink') }}
          </router-link>
        </div>
      </b-form>
    </ValidationObserver>
  </Page>
</template>

<script lang="ts">
import KeyPad from '@/components/Elements/KeyPad/KeyPad.vue'
import Page from '@/components/Elements/Page/Page.vue'
import { HandleErrorsMixin } from '@/mixins/handlers'
import store from '@/store/index'
import { anonymousAxios } from '@/utils/blytz-axios'
import { Dict } from '@/utils/interfaces'
import { resolveLoginDestination } from '@/views/Auth/LoginView/loginDestination'
import { BOverlay } from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mixins } from 'vue-class-component'
import { Component } from 'vue-property-decorator'
import { mapGetters, mapMutations } from 'vuex'

Component.registerHooks(['beforeRouteUpdate'])
@Component({
  components: {
    BOverlay,
    KeyPad,
    Page,
    ValidationProvider,
    ValidationObserver
  },
  computed: {
    ...mapGetters({ merchant: 'currentMerchant' })
  },
  beforeRouteEnter: async (to, from, next) => {
    if (await store.getters.isAuthenticated) {
      const nextParam = Array.isArray(to.query?.next)
        ? to.query.next[0]
        : (to.query?.next as string | undefined)

      const destination = await resolveLoginDestination(nextParam || undefined)
      next(destination)
    } else {
      const merchantID = to.params?.merchantID
      if (merchantID) {
        await store.dispatch('fetchMerchant', merchantID)
      }

      next()
    }
  },
  methods: {
    ...mapMutations(['openDrawer'])
  }
})
export default class LoginView extends mixins(HandleErrorsMixin) {
  email = ''
  password = ''
  phone = ''
  tokenInvalid = false
  hasPin: boolean | null = null
  hasPassword: boolean | null = null
  busy = false

  mounted() {
    if (this.hasToken) this.getUsername()
  }

  pinLengthCheck(value: string) {
    if (value && value.length === 4) {
      this.handleLogin()
    }
  }

  async getUsername() {
    const payload = {
      id: this.$route.query.id,
      token: this.$route.query.token,
      page_size: 100
    }

    try {
      const res = await anonymousAxios.get(this.$endpoints.paymentLink(), {
        params: payload
      })
      this.email = res.data.email
      this.hasPin = res.data.has_pin == null ? true : Boolean(res.data.has_pin)
      this.hasPassword =
        res.data.has_password == null ? true : Boolean(res.data.has_password)
    } catch {
      this.tokenInvalid = true
      // TODO(follow-up): the only toast with no variant (Bootstrap's white default), fired when a texted
      // PIN link fails to resolve. Error (manual dismiss, follows the customer onto SendLoginLink) or
      // info is a copy decision, not a styling one, so it is deliberately left unchanged here.
      // eslint-disable-next-line no-restricted-syntax
      this.$bvToast.toast(
        `Your pin token is invalid, please login with email and password`,
        {
          title: 'Invalid Token',
          toaster: 'b-toaster-bottom-center',
          autoHideDelay: 5000,
          appendToast: true
        }
      )

      const nextPath = this.$route.query.next as string | undefined
      const accountId = nextPath
        ? this.$router.resolve(nextPath).route.params?.accountID ||
          nextPath.match(/\/account\/([^?/]+)/)?.[1]
        : undefined

      if (accountId) {
        this.$router.push({
          name: 'SendLoginLink',
          params: { accountID: accountId }
        })
      }
    }
  }

  async handleLogin() {
    if (this.busy) {
      return
    }

    const payload: Dict = {
      email: this.email,
      password: this.password
    }

    if (this.isPinLogin) {
      payload['token_id'] = this.$route.query.id
      payload['token'] = this.$route.query.token
    }

    this.busy = true
    try {
      await this.$store.dispatch('login', payload)
    } catch (err: any) {
      if (err.response) {
        this.handleAPIErrors(err.response)
        this.password = ''
      } else {
        console.log('login error', err)
      }
      this.busy = false

      return
    }
    this.busy = false

    await this.$nextTick()

    const next = Array.isArray(this.$route.query.next)
      ? this.$route.query.next[0]
      : this.$route.query.next

    const destination = await resolveLoginDestination(next || undefined)
    this.$router.push(destination)
  }

  get hasToken() {
    return (
      this.$route?.query?.id && this.$route?.query?.token && !this.tokenInvalid
    )
  }

  get isPinLogin() {
    return this.hasToken && this.$route?.query?.method !== 'email'
  }

  get loginHelpDestination() {
    return this.isPinLogin ? 'ForgotPin' : 'ForgotPassword'
  }

  get showLoginHelp() {
    return this.isPinLogin ? this.hasPin !== false : this.hasPassword !== false
  }

  get pinLoginLink() {
    const query: Dict = { ...this.$route.query }
    delete query.method

    return {
      name: this.$route.name as string,
      params: this.$route.params,
      query
    }
  }

  get emailLoginLink() {
    const query: Dict = { ...this.$route.query, method: 'email' }

    return {
      name: this.$route.name as string,
      params: this.$route.params,
      query
    }
  }
}
</script>

<style lang="scss" scoped>
.login-form {
  flex: 1;
  display: flex;
  flex-direction: column;
}

#password {
  margin-top: 25px;
}

.login-help,
.send-link {
  margin: 10px;
  text-decoration: underline;
  text-align: center;
}

.auth-tabs {
  display: flex;
  margin-bottom: 25px;
  border-bottom: 1px solid $color-border-decorative;
}

.auth-tab {
  flex: 1;
  padding: 10px;
  text-align: center;
  color: $color-text-secondary;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition:
    color 100ms ease-in-out,
    border-color 100ms ease-in-out;

  &.active {
    color: $blue;
    font-weight: bold;
    border-bottom-color: $blue;
  }
}

.account-links {
  justify-content: space-between;
}

.send-link {
  margin-top: 30px;
}

button {
  margin-top: 35px;
}

.auth-setup {
  text-align: center;
  padding: 20px 0;

  &__copy {
    color: $steel-gray;
    margin-bottom: 15px;
  }

  &__link {
    text-decoration: underline;
    color: $blue;
    font-weight: bold;
  }
}

.auth-loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}
</style>
